import { ToursList } from '@/components/tours';
import { tourTypeCrudConfig, type TourType } from '@/config/objects/tour-types';
import { tourCrudConfig, type Tour } from '@/config/objects/tours';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Tour Type detail page
 */
export function TourTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [tourType, setTourType] = React.useState<TourType | null>(null);

  /**
   * Delete tour type mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (tourTypeId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/tour-types/${tourTypeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate tour types list to refresh data
        queryClient.invalidateQueries({ queryKey: ['TourType'] });
        navigate('/tour-types');
      } else {
        throw new Error(response.error || 'Failed to delete tour type');
      }
    },
  });

  // Create custom config for tour type-specific tours
  const tourTypeTourConfig: CrudConfig<Tour> = useMemo(
    () => ({
      ...tourCrudConfig,
      entityName: 'Tour',
      entityNamePlural: 'Tours',
      endpoints: {
        ...tourCrudConfig.endpoints,
        list: `/tour-types/${id}/tours`,
        delete: (tourId: string) => `/tour-types/${id}/tours/${tourId}`,
      },
      listView: {
        ...tourCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/tours',
            where: tourType
              ? {
                  tourTypeId: {
                    not: tourType.id,
                  },
                }
              : undefined,
            onAdd: async (tour: Tour) => {
              await apiClient.post(`/tour-types/${id}/tours`, { tourId: tour.id });
              toast.success('Tour added to tour type');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (tour: Tour) => {
              await apiClient.delete(`/tour-types/${id}/tours/${tour.id}`);
              toast.success('Tour removed from tour type');
            },
          },
        },
      },
    }),
    [tourType, id, apiClient],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Tour Type Not Found</h2>
          <p className="text-muted-foreground">The requested tour type could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this tour type
   */
  const handleEdit = (tourType: TourType) => {
    navigate(`/tour-types/${tourType.id}/edit`);
  };

  /**
   * Handle tour type deletion
   */
  const handleDelete = async (tourType: TourType) => {
    return toast.promise(deleteMutation.mutateAsync(tourType.id), {
      loading: 'Deleting tour type...',
      success: 'Tour type deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete tour type',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Tour Types', href: '/tour-types' },
    { label: tourType?.name || 'Tour Type', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={tourTypeCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(tourTypeData: TourType) => setTourType(tourTypeData)}
        />

        {/* Tours Section */}
        <ToursList config={tourTypeTourConfig} className="p-0" baseUrl="/tours" />
      </div>
    </PageContainer>
  );
}
